import api from '../../../services/api';
import { LANGUAGE_PREFERENCES_VERSION } from './constants';
import {
  DEFAULT_LANGUAGE_PREFERENCES,
  type LanguageCode,
  type LanguagePreferences,
} from './types';

interface LanguagePreferencesApiResponse {
  languageCode?: unknown;
  language_code?: unknown;
  version?: unknown;
  updatedAt?: unknown;
  updated_at?: unknown;
  preferences?: unknown;
}

const isLanguageCode = (value: unknown): value is LanguageCode => {
  return value === 'en' || value === 'id';
};

const normalize = (
  payload: LanguagePreferencesApiResponse | null | undefined
): LanguagePreferences => {
  if (!payload) {
    return DEFAULT_LANGUAGE_PREFERENCES;
  }

  const source =
    payload.preferences && typeof payload.preferences === 'object'
      ? (payload.preferences as LanguagePreferencesApiResponse)
      : payload;

  const languageCodeCandidate =
    source.languageCode ?? source.language_code ?? DEFAULT_LANGUAGE_PREFERENCES.languageCode;

  return {
    languageCode: isLanguageCode(languageCodeCandidate)
      ? languageCodeCandidate
      : DEFAULT_LANGUAGE_PREFERENCES.languageCode,
    version: typeof source.version === 'number' ? source.version : LANGUAGE_PREFERENCES_VERSION,
    updatedAt:
      typeof source.updatedAt === 'string'
        ? source.updatedAt
        : typeof source.updated_at === 'string'
          ? source.updated_at
          : undefined,
  };
};

export const languagePreferencesService = {
  async get(): Promise<LanguagePreferences> {
    const response = await api.get<LanguagePreferencesApiResponse>('/users/me/preferences');
    return normalize(response.data);
  },

  async update(preferences: LanguagePreferences): Promise<LanguagePreferences> {
    const payload = {
      languageCode: preferences.languageCode,
      version: preferences.version || LANGUAGE_PREFERENCES_VERSION,
    };
    const response = await api.put<LanguagePreferencesApiResponse>('/users/me/preferences', payload);
    if (!response.data || (typeof response.data === 'object' && Object.keys(response.data).length === 0)) {
      return {
        ...payload,
        updatedAt: new Date().toISOString(),
      };
    }
    return normalize(response.data);
  },
};
